import React, { createContext, useContext, useMemo, useReducer } from 'react';

import DisplayAlert from './Alert.jsx';
import AlertContext, {
  DispatchAlert,
  TEMPORARY_ALERT,
  CLEAR_ALERT,
  reduceAlerts,
  SnackbarAlert,
  Alert
} from './AlertContext.ts';

const DispatchAlertContext = createContext((() => undefined) as DispatchAlert);

export interface AlertManagerProps {
  /**
   * Any children to be rendered
   */
  children: React.ReactNode;
}

/**
 * The AlertManager provides an AlertContext to all children so that errors can be displayed with the Alert component
 */
const AlertManager: React.FC<AlertManagerProps> = ({ children }) => {
  const [alerts, dispatchAlert] = useReducer(reduceAlerts, []);
  const ctx = useMemo(
    () => ({
      alerts: alerts.filter(({ type }) => type !== TEMPORARY_ALERT) as Alert[],
      snackbars: alerts.filter(({ type }) => type === TEMPORARY_ALERT) as SnackbarAlert[],
      dispatchAlert
    }),
    [alerts]
  );

  return (
    <DispatchAlertContext.Provider value={dispatchAlert}>
      <AlertContext.Provider value={ctx}>
        {ctx.alerts.map(({ id, type, message, onDismiss, isDismissable }) => (
          <DisplayAlert
            key={id}
            type={type}
            isDismissable={isDismissable}
            onChange={isOpen => {
              if (isOpen) {
                return;
              }

              dispatchAlert({ type: CLEAR_ALERT, id });

              if (onDismiss) {
                onDismiss();
              }
            }}
          >
            {message}
          </DisplayAlert>
        ))}
        {children}
      </AlertContext.Provider>
    </DispatchAlertContext.Provider>
  );
};

export function useDispatchAlert(): DispatchAlert {
  return useContext(DispatchAlertContext);
}

export default AlertManager;
